import React, {useState, useEffect} from "react";
import {
    FileSearchOutlined,
    IssuesCloseOutlined,
    CheckCircleOutlined,
    ExceptionOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {ShiftScoreTablePropTypes} from "./ShiftScoreComponentPropTypes";
import {ActionButtons, TableContainer} from "./ComponentStyles";
import {Tooltip, Popconfirm, Tag, Button} from "antd";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {ShiftScoreDetailModal, ShiftScoreObjectionModal} from "./";
import CourierReportProblemModal from "./CourierReportProblemModal/CourierReportProblemModal";
import {LateStartIcon, EarlyEndIcon, EndTimeSpan} from "./ShiftScoreTableStyled";
import {getConstant, isConstantExist, minuteToString} from "../../../utils/helpers";
import {ObjectionDetailModal} from "../objectionDetail/ObjectionDetailModal";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import {courierStatuses} from "../../../constants/constants";

const ShiftScoreTable = ({
    data,
    setData,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    setReloadCount,
    dataCount,
    filters
}: ShiftScoreTablePropTypes) => {
    const {t} = useTranslation(["shiftScore", "shiftReportStatuses", "courierStatuses"]);
    const stores = useStore();
    const [selectedItem, setSelectedItem] = useState(null);
    const [approveLoading, setApproveLoading] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [open, setOpen] = useState(false);
    const [objectionType, setObjectionType] = useState("objectionModal");

    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [objectionDetailId, setObjectionDetailId] = useState(null);

    /*    useEffect(() => {
        const collection = document.getElementsByClassName("lastShift");
        const lastShift = Array.from(collection)[0];
        if (lastShift && !filters?.currentStatus==="all") {
            lastShift.innerHTML =
                '<tr class="ant-table-row ant-table-row-level-0 gray">\
                <td colspan="11" class="ant-table-cell" style="text-align:center; padding-right: 5em">\
                ⬆️ Aktif Vardiyalar ⬆️ \
                </td></tr>';
        }
    }, [data]); */

    const closeUpdateModal = ({reloadData = false}) => {
        if (reloadData) {
            setReloadData();
        }
        setSelectedItem();
        setModalType();
    };

    const approveShiftReport = async reportId => {
        setApproveLoading(true);
        const result = await stores.shiftStore.approveShiftReport({reportId});
        if (result) {
            setData({
                rows: data.map(d => {
                    if (d?._id?.toString() === reportId.toString()) {
                        d.status = "approved";
                    }
                    return d;
                }),
                dataCount
            });
        }
        setSelectedItem();
        setApproveLoading(false);
    };

    const createObjectionPermission = item => {
        if (item?.hasObjection) {
            return false;
        }
        if (stores.meStore.checkRole("isOperationUser") || stores.meStore.checkRole("isAdmin")) {
            return (
                !item.status ||
                ["waitingApprove", "needFillPackage", "approved"].includes(item.status)
            );
        } else if (stores.meStore.checkRole("isCompanyUser")) {
            return !item.status || ["waitingApprove"].includes(item.status);
        } else {
            return false;
        }
    };
    const renderActionButtons = (id, item) => {
        const permisions = {
            showReportDetail: stores.meStore.checkRole("notVisibleForCompanyUser"),
            createObjection: createObjectionPermission(item),
            showObjection:
                !["waitingApprove", "needFillPackage", "approved"].includes(item.status) &&
                item?.hasObjection,
            approveReport:
                (stores.meStore.checkRole("isCompanyUser") ||
                    stores.meStore.checkRole("isAdmin") ||
                    !stores.meStore.checkRole("isOperationUser")) &&
                (!item.status || ["waitingApprove", "needFillPackage"].includes(item.status)) &&
                !item?.hasObjection
        };

        return item.isActiveShift ? (
            <Button
                onClick={() => {
                    setSelectedItem(item);
                    setModalType("courierReportProblemModal");
                }}
            >
                {t("shiftScore.reportError", {ns: "shiftScore"})}
            </Button>
        ) : (
            <ActionButtons>
                {permisions.showReportDetail && (
                    <Tooltip title={t("shiftScore.actions.showReportDetail", {ns: "shiftScore"})}>
                        <CustomButton
                            data-testid="detailButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setModalType("detailModal");
                            }}
                            icon={<FileSearchOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.approveReport && (
                    <Tooltip
                        title={
                            item?.status !== "needFillPackage"
                                ? t("shiftScore.actions.approveReport", {ns: "shiftScore"})
                                : t("shiftScore.actions.tryApproveWhenNeedFillPackage", {
                                      ns: "shiftScore"
                                  })
                        }
                        placement="bottom"
                    >
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                setSelectedItem(item);
                                setReloadCount();
                                await approveShiftReport(item._id);
                            }}
                            onCancel={() => setOpen(false)}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                            disabled={item.status === "needFillPackage"}
                        >
                            <CustomButton
                                loading={approveLoading && selectedItem?._id === id}
                                data-testid="objectionButton"
                                icon={<CheckCircleOutlined />}
                                disabled={item.status === "needFillPackage"}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
                {permisions.createObjection && (
                    <Tooltip
                        title={renderShiftObjectionTitle(getObjectionModalType(item))}
                        placement="bottom"
                    >
                        <CustomButton
                            data-testid="objectionButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setModalType("objectionModal");
                                setObjectionType(getObjectionModalType(item));
                            }}
                            icon={<IssuesCloseOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.showObjection && (
                    <Tooltip
                        title={t("shiftScore.actions.showObjectionDetail", {ns: "shiftScore"})}
                    >
                        <CustomButton
                            data-testid="objectionButton"
                            onClick={() => {
                                setDetailModalVisible(true);
                                setObjectionDetailId(item.objections[0]._id);
                            }}
                            icon={<ExceptionOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const renderStatus = (item, status) => {
        const ACTIVE_COLOR = "processing";
        const INACTIVE_COLOR = "purple";
        const APPROVED_COLOR = "green";
        const IN_REVIEW_COLOR = "red";
        const WAITING_APPROVE_COLOR = "blue";
        const NEED_FILL_PACKAGE = "";

        let tag = null;
        if (item.isActiveShift && item.startTime) {
            tag = (
                <Tag color={ACTIVE_COLOR}>
                    {t("shiftScore.shiftStatuses.activeShift", {ns: "shiftScore"})}
                </Tag>
            );
        } else if (item.isActiveShift) {
            tag = (
                <Tag color={INACTIVE_COLOR}>
                    {t("shiftScore.shiftStatuses.noEntry", {ns: "shiftScore"})}
                </Tag>
            );
        } else {
            switch (status) {
                case "approved":
                    tag = (
                        <Tag color={APPROVED_COLOR}>
                            {t("shiftScore.shiftStatuses.approved", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "inReview":
                    tag = (
                        <Tag color={IN_REVIEW_COLOR}>
                            {t("shiftScore.shiftStatuses.objectionProcess", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "waitingApprove":
                    tag = (
                        <Tag color={WAITING_APPROVE_COLOR}>
                            {t("shiftScore.shiftStatuses.waitingApprove", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "needFillPackage":
                    tag = (
                        <Tag color={NEED_FILL_PACKAGE} status="processing">
                            {t("shiftScore.shiftStatuses.needFillPackage", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                default:
                    break;
            }
        }
        return tag;
    };

    const getRowClassName = item => {
        if (item.isLastshift) {
            return "lastShift";
        }
        return item.startTime && stores.meStore.checkRole("notVisibleForCompanyUser")
            ? (() => {
                  const diff = moment(item.startTime, "DD-MM-YYYY HH:mm:ss").diff(
                      moment(`${item.date}T${item.shiftStartTime}`, "DD-MM-YYYYTHH:mm"),
                      "minutes"
                  );
                  switch (true) {
                      case diff > 0 && diff < 5:
                          return "yellow";
                      case diff > 5 && diff < 15:
                          return "orange";
                      case diff > 15:
                          return "red";
                      default:
                          return null;
                  }
              })()
            : "gray";
    };

    const getWorkTime = item => {
        if (item.isActiveShift && item.startTime) {
            const diff = moment().diff(moment(item.startTime, "DD-MM-YYYY HH:mm:ss"), "minutes");
            return diff;
        } else if (item.workTimeMinute) {
            return item.workTimeMinute;
        } else {
            return 0;
        }
    };

    const renderWorkingMinuteString = workingMinute => {
        const hours = Math.floor(workingMinute / 60);
        const remainingMinutes = workingMinute % 60;

        if (hours > 0) {
            return `${hours} ${t("units.hour.short", {
                ns: "common"
            })} ${remainingMinutes} ${t("units.minute.short", {ns: "common"})}`;
        } else {
            return `${remainingMinutes} ${t("units.minute.short", {ns: "common"})}`;
        }
    };

    const renderLateStartIcon = lateStart => {
        return stores.meStore.checkRole("notVisibleForCompanyUser") && lateStart > 0 ? (
            <Tooltip
                title={
                    lateStart
                        ? `${lateStart}${t("units.minute.short", {
                              ns: "common"
                          })} ${t("shiftScore.lateShiftStart", {
                              ns: "shiftScore"
                          }).toLocaleLowerCase()}`
                        : 0
                }
            >
                <LateStartIcon style={{marginLeft: "5px"}} />
            </Tooltip>
        ) : (
            ""
        );
    };

    const renderEarlyEndIcon = earlyEnd => {
        return stores.meStore.checkRole("notVisibleForCompanyUser") && earlyEnd > 0 ? (
            <Tooltip
                title={
                    earlyEnd
                        ? `${earlyEnd}${t("units.minute.short", {
                              ns: "common"
                          })} ${t("shiftScore.earlyShiftEnd", {
                              ns: "shiftScore"
                          }).toLocaleLowerCase()}`
                        : 0
                }
            >
                <EarlyEndIcon />
            </Tooltip>
        ) : (
            ""
        );
    };

    const renderActualShiftCell = row => {
        const {startTime, endTime, lateStart, earlyEnd, isActiveShift} = row;
        if (!startTime && !endTime) {
            return <span>-</span>;
        }

        return (
            <>
                <span data-testid="startTime-hours">
                    {row.startTime
                        ? moment(row.startTime, "DD.MM.YYYY HH:mm").format("HH:mm")
                        : "-"}
                    {row?.lateStart && renderLateStartIcon(row?.lateStart)}
                </span>
                {row.isActiveShift ? (
                    <>
                        - <b style={{color: "gray"}}> .....</b>
                    </>
                ) : (
                    <>
                        <EndTimeSpan data-testid="endTime-hours">
                            {row.endTime
                                ? `- ${moment(row.endTime, "DD.MM.YYYY HH:mm").format("HH:mm")}`
                                : "-"}
                             
                        </EndTimeSpan>
                    </>
                )}
                {row?.earlyEnd > 0 && renderEarlyEndIcon(row?.earlyEnd)}
            </>
        );
    };

    const renderShiftObjectionTitle = row => {
        return row === "updateModal"
            ? t("shiftScore.actions.updateReport", {ns: "shiftScore"})
            : t("shiftScore.actions.objection", {ns: "shiftScore"});
    };

    const getObjectionModalType = row => {
        if (stores.meStore.checkRole("isOperationUser") || stores.meStore.checkRole("isAdmin")) {
            return "updateModal";
        } else {
            const {date, shiftStartTime, shiftEndTime} = row;

            const shiftStart = moment(`${date} ${shiftStartTime}`, "DD-MM-YYYY HH:mm").tz(
                "Europe/Istanbul"
            );
            const shiftEnd = moment(`${date} ${shiftEndTime}`, "DD-MM-YYYY HH:mm").tz(
                "Europe/Istanbul"
            );
            shiftEnd.isBefore(shiftStart) && shiftEnd.add(1, "days");

            const diff = moment()
                .tz("Europe/Istanbul")
                .diff(shiftEnd, "minutes");

            diff < 120;
            let modaltype = diff < 120 ? "updateModal" : "objectionModal";
            return modaltype;
        }
    };
    return (
        <>
            {" "}
            <ObjectionDetailModal
                isVisible={detailModalVisible}
                setVisible={setDetailModalVisible}
                objectionId={objectionDetailId}
                setReloadData={setReloadData}
                reloadData={false}
            ></ObjectionDetailModal>
            <CourierReportProblemModal
                shift={selectedItem}
                visibility={modalType === "courierReportProblemModal" && Boolean(selectedItem?._id)}
                onCancel={() => closeUpdateModal({reloadData: false})}
            ></CourierReportProblemModal>
            <ShiftScoreDetailModal
                shiftScore={selectedItem}
                visibility={modalType === "detailModal" && Boolean(selectedItem?._id)}
                onCancel={() => closeUpdateModal({reloadData: false})}
            />
            <ShiftScoreObjectionModal
                shiftScore={selectedItem}
                visibility={modalType === "objectionModal" && Boolean(selectedItem?._id)}
                objectionType={objectionType}
                onCancel={() => closeUpdateModal({reloadData: false})}
                closeModal={() => closeUpdateModal({reloadData: true})}
            />
            <TableContainer
                size="middle"
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: 10 + (data?.some(d => d.isLastshift) ? 1 : 0)
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="shiftTable"
                loading={loading}
                display={data.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    data.length > 0 && t("fields.date", {ns: "common"}) + ":",
                    t("fields.courier", {ns: "common"}),
                    t("fields.company", {ns: "common"}),
                    t("fields.subcompany", {ns: "common"}),
                    t("shiftScore.scheduledShift", {ns: "shiftScore"}),
                    t("shiftScore.actualShift", {ns: "shiftScore"}),
                    t("shiftScore.workTime", {ns: "shiftScore"}),
                    t("shiftScore.packageCount", {ns: "shiftScore"}),
                    t("shiftScore.verifiedPackageCount", {ns: "shiftScore"}),
                    t("actions.action", {ns: "common"}),
                    "Kurye Durumu (Anlık)",
                    t("fields.status", {ns: "common"})
                ].filter((data, index) => {
                    if (
                        (!stores.meStore.checkRole("userPermission") ||
                            stores.meStore.auth?.companies?.length === 1) &&
                        index === 2
                    ) {
                        return false;
                    }
                    return true;
                })}
                rowClassName={item => getRowClassName(item)}
            >
                <TableContainer.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={date => (
                        <span data-testid="reportDate">
                            {!date ? "-" : moment(date, "DD-MM-YYYY").format("DD.MM.YYYY")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="fullname"
                    title={t("fields.courier", {ns: "common"})}
                    render={(fullname, data) => (
                        <span data-testid="fullname">
                            {stores.meStore.checkRole("courierDetail") ? (
                                <CourierLink
                                    target="_blank"
                                    to={`/couriers/${data?.courierId ||
                                        data?.beforeLog?.courierId}`}
                                >
                                    {fullname}
                                </CourierLink>
                            ) : (
                                fullname
                            )}
                        </span>
                    )}
                />

                {(stores.meStore.checkRole("userPermission") ||
                    stores.meStore.auth?.companies?.length !== 1) && (
                    <TableContainer.Column
                        dataIndex="companyName"
                        title={t("fields.company", {ns: "common"})}
                        render={(companyName, row) => (
                            <>
                                <span data-testid="companyName">{companyName}</span>
                            </>
                        )}
                    />
                )}

                {(stores.meStore.checkRole("userPermission") ||
                    stores.meStore.auth?.subCompany === undefined ||
                    stores.meStore.auth?.subcompanies?.length > 1) && (
                    <TableContainer.Column
                        dataIndex="subCompanyName"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={(subCompanyName, row) => (
                            <>
                                <span data-testid="subCompanyNmae">{subCompanyName}</span>
                            </>
                        )}
                    />
                )}
                <TableContainer.Column
                    dataIndex="shiftStartTime"
                    title={t("shiftScore.scheduledShift", {ns: "shiftScore"})}
                    render={(shiftStartTime, row) => (
                        <span data-testid="date">
                            {[row?.shiftStartTime, row?.shiftEndTime].join(" - ")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="startTime"
                    title={t("shiftScore.actualShift", {ns: "shiftScore"})}
                    render={(startTime, row) => <span>{renderActualShiftCell(row)}</span>}
                />

                <TableContainer.Column
                    dataIndex="workTimeMinute"
                    title={t("shiftScore.workTime", {
                        ns: "shiftScore"
                    })}
                    render={(workTimeMinute, item) => (
                        <>
                            <span data-testid="workTimeMinute">
                                {minuteToString(getWorkTime(item))}
                            </span>
                        </>
                    )}
                />

                <TableContainer.Column
                    dataIndex="packageCount"
                    title={t("shiftScore.packageCount", {ns: "shiftScore"})}
                    render={(packageCount, item) => (
                        <>
                            <span data-testid="packageCount">
                                {packageCount || 0}
                                {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                    item?.reason && (
                                        <Tooltip title={item?.reason}>
                                            {" "}
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    )}
                            </span>
                        </>
                    )}
                />
                <TableContainer.Column
                    dataIndex="verifiedPackageCount"
                    title={t("shiftScore.verifiedPackageCount", {ns: "shiftScore"})}
                    render={(verifiedPackageCount, item) => (
                        <>
                            <span data-testid="verifiedPackageCount">
                                {verifiedPackageCount || 0}
                                {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                    item?.reason && (
                                        <Tooltip title={item?.reason}>
                                            {" "}
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    )}
                            </span>
                        </>
                    )}
                />

                <TableContainer.Column
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={(_id, item) => <span>{renderActionButtons(_id, item)} </span>}
                />
                {filters?.currentStatus && filters?.currentStatus !== "all" ? (
                    <TableContainer.Column
                        title={
                            <>
                                Kurye Durumu
                                <br />
                                (Anlık)
                            </>
                        }
                        dataIndex="courierstate"
                        render={(_, item) => (
                            <Tag data-testid="courierStatus">
                                {isConstantExist(courierStatuses, item?.courierstate?.status)
                                    ? t(
                                          getConstant(courierStatuses, item?.courierstate?.status)
                                              .translation_key,
                                          {
                                              ns: "courierStatuses"
                                          }
                                      )
                                    : item?.courierstate?.status}
                            </Tag>
                        )}
                    />
                ) : (
                    <></>
                )}
                <TableContainer.Column
                    title={t("fields.status", {ns: "common"})}
                    dataIndex="status"
                    render={(status, item) => <span>{renderStatus(item, status)}</span>}
                />
            </TableContainer>
        </>
    );
};

export default ShiftScoreTable;
